'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

export interface CompanySuggestion {
  euId: string;
  name: string;
  city: string | null;
  registerId: string | null;
  registerType: string | null;
  registerCourt: string | null;
  faviconUrl: string | null;
}

const COMPANY_SUGGEST_KEY = ['company-suggest'] as const;

/** The suggest route answers from the first character (biggest revenue first). */
export const COMPANY_SUGGEST_MIN_CHARS = 1;

async function fetchCompanySuggest(
  query: string,
  limit: number,
): Promise<CompanySuggestion[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const res = await fetch(`/api/companies/suggest?${params.toString()}`);
  if (!res.ok) throw new Error(`/api/companies/suggest returned ${res.status}`);
  const json = (await res.json()) as { results?: CompanySuggestion[] };
  return json.results ?? [];
}

/**
 * Company name suggestions for a search box. Pass an already debounced query;
 * shorter queries than COMPANY_SUGGEST_MIN_CHARS stay idle.
 */
export function useCompanySuggest(query: string, limit = 6) {
  const q = query.trim();
  return useQuery({
    queryKey: [...COMPANY_SUGGEST_KEY, q, limit] as const,
    queryFn: () => fetchCompanySuggest(q, limit),
    enabled: q.length >= COMPANY_SUGGEST_MIN_CHARS,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}
